import {inject, Injectable} from '@angular/core';
import {User} from "../types/smm/smm-person.types";
import {lastValueFrom} from "rxjs";
import {UrlEnum} from "../types/url-enum";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {UrlService} from "./url.service";
import {AppDB} from "app/core/indexDB/db";
import {DomSanitizer} from "@angular/platform-browser";
import {ExtendedErrorInfo, RejectReason} from "../types/smm/error";
import {ClinicInfo} from "../types/clinic.types";

@Injectable({ providedIn: 'root' })
export class ImgService {

    // ------- class members-------------------
    private _httpClient = inject(HttpClient);
    private _urlService : UrlService = inject(UrlService);
    private _sanitizer: DomSanitizer = inject(DomSanitizer);

    // ------- public methods members-------------------
    public async uploadImage(imageID: string, urlEndpoint:string, file: File):Promise<Blob> {
        try{
            const blob:Blob = await this.cropAndFit(file, 200,200);
            const formData: FormData = new FormData();
            let blb: Blob = new Blob([blob], { type: 'image/jpeg' });
            formData.append('file', blb, "avatar.jpeg");
            await ImgService.cacheImg(ImgType.avatar, imageID, blob);
            // Send formData as the payload instead of blob
            await lastValueFrom(
                this._httpClient.post<string>(
                    urlEndpoint,
                    formData
                )
            );
            return blob;
        }catch(e){
            throw new Error(
                "There was an error during communication with server.",
                {cause: new ExtendedErrorInfo(RejectReason.BECallError, e.message())}
            )
        }
    }

    public async deleteImage(imgID: string, endpoint:string):Promise<boolean> {
        try{
            await ImgService.deleteCachedImg(imgID, ImgType.avatar);
            // Send formData as the payload instead of blob
            await lastValueFrom(this._httpClient.delete<string>(endpoint));
            return true;
        }catch(e){
            throw new Error(
                "There was an error during communication with server.",
                {cause: new ExtendedErrorInfo(RejectReason.BECallError, e.message())}
            )
        }
    }

    public async getImage(imgID:string, endpoint:string):Promise<Blob> {
        let img:Blob;
        try{
            img = await ImgService.getCachedImg(imgID, ImgType.avatar);
            if(img){
                return img;
            }
        }catch(err){
            //we don't reject yet because we have one last trick :p ... asking BE
            console.error(`There was an issue when tried to retrieve cached avatar image with ID ${imgID}: Err is`, err);
        }

        try {
            let httpHeaders = new HttpHeaders()
                .set('Accept', "image/webp,*/*");

            img = await lastValueFrom(
                this._httpClient.get<Blob>(
                    endpoint, {
                        headers: httpHeaders,
                        responseType: 'blob' as 'json'
                    }
                )
            );

            await ImgService.cacheImg(ImgType.avatar, imgID, img);
            if(img){
                return img;
            }
        }catch(err){
            throw  new Error("There was an error during communication with server.", {cause: new ExtendedErrorInfo(RejectReason.BECallError, err.message())});
        }
    }

    public async uploadAvatar(user: User, file: File):Promise<Blob> {
        return this.uploadImage(
            user.id,
            this._urlService.getUrl(UrlEnum.private_add_user_profile_pic).replace('{userId}', user.id),
            file);
    }

    public async deleteAvatar(userID: string):Promise<boolean> {
        return this.deleteImage(
          userID,
          this._urlService.getUrl(UrlEnum.private_add_user_profile_pic).replace('{userId}', userID)
        );
    }

    public async getAvatar(user:User):Promise<Blob> {
        if(!user.userProfilePath){
            return null;
        }
        return this.getImage(user.id, this._urlService.getUrl(UrlEnum.private_add_user_profile_pic).replace('{userId}', user.id));
    }

    public async uploadClinicLogo(clinicID:string, file: File):Promise<Blob> {
        return this.uploadImage(
            clinicID,
            this._urlService.getUrl(UrlEnum.private_clinic_logo).replace('{clinicId}', clinicID),
            file);
    }

    public async deleteClinicLogo(clinicID: string):Promise<boolean> {
        return this.deleteImage(
            clinicID,
            this._urlService.getUrl(UrlEnum.private_clinic_logo).replace('{clinicId}', clinicID)
        );
    }

    public async getClinicLogo(clinic:ClinicInfo):Promise<Blob> {
        if(!clinic.logoLocation){
            return null;
        }
        return this.getImage(clinic.id, this._urlService.getUrl(UrlEnum.private_clinic_logo).replace('{clinicId}', clinic.id));
    }

    cropAndFit(file:File, img_w:number, img_h:number):Promise<Blob>{
        const reader = new FileReader();
        reader.readAsDataURL(file);
        return new Promise((resolve, reject) => {
            reader.onload = async () => {
                try {
                    const base64ImgString = await this.resizeImage(reader.result as string, img_w, img_h);
                    const base64Response = await fetch(`${base64ImgString}`);
                    //cache it
                    const blob = await base64Response.blob();
                    resolve(blob);
                } catch (error) {
                    reject(error); /*TODO: Error handling */
                }
            };
            reader.onerror = error => reject(error); /*TODO: Error handling */
        });
    }

    private resizeImage(imageURL: any, w:number, h:number): Promise<string> {
        return new Promise((resolve) => {
            const image = new Image();
            image.onload = function () {
                const canvas = document.createElement('canvas');
                const sizes:ResultInfo = ImgService.getSizeAndPosition({real_w: image.naturalWidth, real_h: image.naturalHeight, desired_w: w, desired_h: h});
                canvas.width = sizes.info.desired_w;
                canvas.height = sizes.info.desired_h;
                const ctx = canvas.getContext('2d');
                if (ctx != null) {
                    ctx.drawImage(image, sizes.x, sizes.y, sizes.info.real_w, sizes.info.real_h, 0,0, sizes.info.desired_w,sizes.info.desired_h);
                }
                const data = canvas.toDataURL('image/jpeg', 1);
                resolve(data);
            };
            image.src = imageURL;
        });
    }

    private static getSizeAndPosition(info:ResizeInfo):ResultInfo{
        //fixing unrealistic resize data (up scaling not allowed)
        if(info.desired_w > info.real_w){
            info.desired_w = info.real_w;
        }
        if(info.desired_h > info.real_h){
             info.desired_h = info.real_h;
        }

        const result:ResultInfo = {
            x:0, y:0,
            info: {
                desired_w:info.desired_w,
                desired_h:info.desired_h,
                real_w:0,
                real_h:0
            }
        };

        result.info.real_w = info.real_w;
        result.info.real_h = result.info.real_w * (info.desired_h/info.desired_w); //computed to keep desired aspect ratio
        result.x = 0;
        result.y = (info.real_h - result.info.real_h)/2;


        if(result.info.real_h > info.real_h){
            result.info.real_h = info.real_h;
            result.info.real_w = result.info.real_h * (info.desired_w/info.desired_h); //computed to keep desired aspect ratio
            result.y = 0;
            result.x = (info.real_w - result.info.real_w)/2;
        }

        return result;
    }

    static cacheImg(pictureType: ImgType, id: string, blob: Blob):Promise<string> {
        return AppDB.getDB().putImage(pictureType, id, blob);
    }

    static getCachedImg(userID:string, pictureType: ImgType):Promise<Blob> {
        return AppDB.getDB().getImage(userID,pictureType);
    }

    static async deleteCachedImg(userID:string, pictureType: ImgType):Promise<boolean> {
        return await AppDB.getDB().deleteImage(userID, pictureType) > 0;
    }

    public setAvatarImgFromBlob(user:User, blob:Blob):void {
        if(blob == null){
            return;
        }
        let objectURL = URL.createObjectURL(blob);
        user.avatar = this._sanitizer.bypassSecurityTrustUrl(objectURL);
    }

    public setClinicLogoFromBlob(clinic:ClinicInfo, blob:Blob):void {
        if(blob == null){
            return;
        }
        let objectURL = URL.createObjectURL(blob);
        clinic.logoLocation = this._sanitizer.bypassSecurityTrustUrl(objectURL);
    }

    //keep it for reference
    // static cacheBlobImg(pictureType: ImgType, userID: string, blob: Blob) {
    //     var reader = new FileReader();
    //     reader.readAsDataURL(blob);
    //     reader.onloadend = function() {
    //         const base64data = reader.result;
    //         if(ImgService.isString(base64data)){
    //             ImgService.cacheImg(pictureType,userID,<string>base64data);
    //         }
    //     }
    // }

    // public static isString(x):boolean {
    //     return Object.prototype.toString.call(x) === "[object String]"
    // }
}

interface ResizeInfo{
    desired_w:number;
    desired_h:number;
    real_w:number;
    real_h:number;
}

interface ResultInfo{
    x:number;
    y:number;
    info: ResizeInfo;
}

export enum ImgType{
    avatar = "avatar",
    xray = "xray",
    other = "other"
}
